<template>
    <!-- 数据库控制台面板（嵌入右侧页签内） -->
    <div class="dbconsole-drawer dbconsole-panel">
        <div class="dbconsole-panel__head">
            <span class="dbconsole-head__name">{{ conn.name }}</span>
            <span class="dbconsole-head__addr">{{ conn.host }}:{{ conn.port }}</span>
            <el-tag size="small" :type="conn.canWrite ? 'warning' : 'info'" effect="plain">
                {{ conn.canWrite ? '可写' : '只读' }}</el-tag>
            <el-tag v-if="conn.prodLock" size="small" type="danger" effect="plain">生产防护</el-tag>
            <!-- 右侧操作：新建块 / 浏览表 / 打开「已保存 SQL 块」小抽屉 -->
            <el-button class="dbconsole-panel__add" size="small" type="primary" plain @click="highlightNew(addBlock())">
                <el-icon class="el-icon--left">
                    <Plus />
                </el-icon>
                新建
            </el-button>
            <el-button class="dbconsole-panel__tables-btn" size="small" @click="openTables()">
                <el-icon class="el-icon--left">
                    <Grid />
                </el-icon>
                表
            </el-button>
            <el-button class="dbconsole-panel__blocks-btn" size="small" @click="blocksDrawer = true">
                <el-icon class="el-icon--left">
                    <Collection />
                </el-icon>
                SQL 块
                <span v-if="blockPage.total" class="dbconsole-panel__blocks-count">{{ blockPage.total }}</span>
            </el-button>
        </div>

        <div class="dbconsole-body">
            <!-- 左侧：SQL 块编辑区 -->
            <section class="dbconsole-left">
                <!-- 块列表：独立滚动容器，滚动条只出现在标题栏下方（与右侧 .dbconsole-right__list 同构） -->
                <div ref="leftList" class="dbconsole-left__list">
                    <div v-if="blocks.length === 0" class="dbconsole-empty">
                        <el-icon :size="22">
                            <EditPen />
                        </el-icon>
                        <p>点击「新建」开始编写 SQL</p>
                    </div>
                    <!-- 进入时展开动画 + 短暂高亮，便于用户看清是哪个块被新增/载入 -->
                    <!-- 动画结束后统一重测编辑器：动画期间（max-height/transform 过渡）CodeMirror 的
                         度量会失效，失效状态下点击会在 CM 内部报错、点不进编辑区（表现为「没插入点、输入无效」） -->
                    <transition-group name="dbconsole-block-anim" @after-enter="onBlockAnimDone"
                        @after-leave="onBlockAnimDone">
                        <div class="dbconsole-block" :class="{ 'is-fresh': block._fresh }" v-for="(block, bi) in blocks"
                            :key="block.uid">
                        <div class="dbconsole-block__head">
                            <!-- 左上角序号标志（倒序：新增的块在最上面，序号最大） -->
                            <span class="dbconsole-block__no">{{ blocks.length - bi }}</span>
                            <div class="dbconsole-block__title">
                                <span class="dbconsole-block__name">{{ block.name || '未保存' }}</span>
                                <el-tag v-if="block.id && block.isShare" size="small" type="success" effect="plain">已共享</el-tag>
                                <el-select v-model="block.database" class="dbconsole-block__db-select" size="small"
                                    placeholder="选择数据库" filterable allow-create default-first-option :loading="dbLoading"
                                    @change="tablesDb = $event">
                                    <el-option v-for="db in databases" :key="db" :label="db" :value="db" />
                                </el-select>
                            </div>
                            <div class="dbconsole-block__ops">
                                <el-button size="small" type="primary" :loading="block.loading" @click="run(block)">
                                    <el-icon class="el-icon--left">
                                        <CaretRight />
                                    </el-icon>
                                    执行全部
                                </el-button>
                                <el-button size="small" type="primary" plain :loading="block.loading" @click="runSelected(block)">
                                    <el-icon class="el-icon--left">
                                        <Select />
                                    </el-icon>
                                    执行选中
                                </el-button>
                                <el-button size="small" type="warning" @click="openSave(block)">
                                    <el-icon class="el-icon--left">
                                        <Star />
                                    </el-icon>
                                    保存
                                </el-button>
                                <el-button size="small" text @click="copySql(block)">
                                    <el-icon class="el-icon--left">
                                        <DocumentCopy />
                                    </el-icon>
                                    复制
                                </el-button>
                                <el-button size="small" text @click="formatBlock(block)">格式化</el-button>
                                <el-button size="small" type="danger" @click="removeBlock(block)">移除</el-button>
                            </div>
                        </div>
                        <div class="dbconsole-block__input">
                            <sql-editor :ref="'sqlEditor-' + block.uid" v-model="block.sqlText" :mode="sqlMode"
                                :tables="tablesFor(block.database)"
                                :table-comments="tableCommentsFor(block.database)"
                                :columns="columnsFor(block.database)"
                                placeholder="在此输入 SQL（输入表名会自动提示当前库的表；Ctrl/Cmd + Enter：有选中则执行选中，否则执行全部）"
                                @run="selected => onRun(block, selected)"
                                @need-columns="tables => onNeedColumns(block.database, tables)" />
                        </div>

                        <!-- 执行结果：单条语句直接展示；多条语句按 tab 分栏（每条语句一个结果页，便于逐个查看/对比） -->
                        <div class="dbconsole-block__result">
                            <template v-if="block.results.length <= 1">
                                <db-result-panel v-if="block.results[0]" :result="block.results[0].result"
                                    :error="block.results[0].error" :loading="block.results[0].loading"
                                    :sql="block.results[0].sql" :column-meta="columnsFor(block.database)"
                                    :can-write="!!conn.canWrite"
                                    :commit-handler="payload => commitResultChanges(block, 0, payload)"
                                    @favorite="rows => openFavorite(block, rows, block.results[0].result, block.results[0].sql)" />
                            </template>
                            <el-tabs v-else v-model="block.activeResult" class="dbconsole-block__tabs">
                                <el-tab-pane v-for="(st, si) in block.results" :key="st.id" :name="st.id" :lazy="true">
                                    <template #label>
                                        <span class="dbconsole-tab">
                                            <span class="dbconsole-tab__no">#{{ si + 1 }}</span>
                                            <span class="dbconsole-tab__sum" :class="'is-' + st.status">
                                                {{ st.summary || '待执行' }}</span>
                                        </span>
                                    </template>
                                    <db-result-panel :result="st.result" :error="st.error" :loading="st.loading" :sql="st.sql"
                                        :column-meta="columnsFor(block.database)"
                                        :can-write="!!conn.canWrite"
                                        :commit-handler="payload => commitResultChanges(block, si, payload)"
                                        @favorite="rows => openFavorite(block, rows, st.result, st.sql)" />
                                </el-tab-pane>
                            </el-tabs>
                        </div>
                        </div>
                    </transition-group>
                </div>
            </section>
        </div>

        <!-- 已保存的 SQL 块：右侧小抽屉（服务端分页 + 名称筛选；默认只看自己创建的，可切换查看共享） -->
        <el-drawer v-model="blocksDrawer" class="dbconsole-blocks-drawer" direction="rtl" size="380px"
            :with-header="false" append-to-body>
            <aside class="dbconsole-right">
                <div class="dbconsole-right__head">
                    <span class="dbconsole-title">SQL 块</span>
                    <span class="dbconsole-right__count">{{ blockPage.total }}</span>
                    <div class="dbconsole-right__switch">
                        <span class="dbconsole-right__switch-label">他人共享</span>
                        <el-switch v-model="showShared" size="small" />
                    </div>
                    <el-button class="dbconsole-right__close" size="small" text @click="blocksDrawer = false">
                        <el-icon>
                            <Close />
                        </el-icon>
                    </el-button>
                </div>
                <el-input v-model="blockKeyword" class="dbconsole-right__search" size="small" clearable
                    placeholder="按名称筛选">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <div class="dbconsole-right__list" v-loading="blockLoading">
                    <el-empty v-if="rightBlocks.length === 0"
                        :description="blockKeyword ? '没有匹配的 SQL 块' : (showShared ? '暂无他人共享的 SQL 块' : '暂无 SQL 块')"
                        :image-size="60" />
                    <div v-for="sb in rightBlocks" :key="sb.id" class="dbconsole-shared">
                        <div class="dbconsole-shared__head">
                            <div class="dbconsole-shared__info">
                                <div class="dbconsole-shared__name">{{ sb.name }}</div>
                                <!-- 数据库/共享等标签单独一行，避免把 SQL 名称挤变形 -->
                                <div v-if="sb.databaseName || sb.isShare || !sb.ownedByMe" class="dbconsole-shared__meta">
                                    <el-tag v-if="sb.databaseName" size="small" type="info" effect="plain">
                                        {{ sb.databaseName }}</el-tag>
                                    <el-tag v-if="sb.isShare" size="small" type="success" effect="plain">共享</el-tag>
                                    <span v-if="!sb.ownedByMe" class="dbconsole-shared__other">{{ sb.ownerName || '他人' }}</span>
                                </div>
                                <div class="dbconsole-shared__desc">{{ sb.description || '—' }}</div>
                            </div>
                            <!-- 操作：载入直接外露；其余（收藏结果 / 编辑 / 共享切换 / 删除）收进菜单 -->
                            <div class="dbconsole-shared__ops">
                                <el-button class="dbconsole-shared__load" size="small" type="primary" plain
                                    @click="loadToEditor(sb)">
                                    <el-icon class="el-icon--left">
                                        <Download />
                                    </el-icon>载入
                                </el-button>
                                <el-dropdown trigger="click" @command="cmd => onBlockCommand(cmd, sb)">
                                    <el-button size="small" text @click.stop>
                                        <el-icon>
                                            <MoreFilled />
                                        </el-icon>
                                    </el-button>
                                    <template #dropdown>
                                        <el-dropdown-menu>
                                            <el-dropdown-item command="favs" class="dbconsole-mi">
                                                <el-icon>
                                                    <Collection />
                                                </el-icon>收藏结果（{{ sb.favoriteCount || 0 }}）
                                            </el-dropdown-item>
                                            <el-dropdown-item command="edit" :disabled="!sb.ownedByMe" class="dbconsole-mi">
                                                <el-icon>
                                                    <EditPen />
                                                </el-icon>编辑
                                            </el-dropdown-item>
                                            <el-dropdown-item command="share" :disabled="!sb.ownedByMe"
                                                class="dbconsole-mi dbconsole-mi--share">
                                                <el-icon>
                                                    <Share />
                                                </el-icon>{{ sb.isShare ? '取消共享' : '共享' }}
                                            </el-dropdown-item>
                                            <el-dropdown-item command="delete" :disabled="!sb.ownedByMe" divided
                                                class="dbconsole-mi dbconsole-mi--delete">
                                                <el-icon>
                                                    <Delete />
                                                </el-icon>删除
                                            </el-dropdown-item>
                                        </el-dropdown-menu>
                                    </template>
                                </el-dropdown>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 底部悬浮胶囊分页条（每页 100 条） -->
                <div v-if="blockPage.total > blockPage.size" class="dbconsole-pager">
                    <el-pagination small background layout="prev, pager, next" :total="blockPage.total"
                        :page-size="blockPage.size" :current-page="blockPage.current" :pager-count="5"
                        @current-change="onBlockPageChange" />
                </div>
            </aside>
        </el-drawer>

        <!-- 「表」浏览器：右侧小抽屉，树形展示当前库所有表名，可展开查看字段与类型 -->
        <el-drawer v-model="tablesDrawer" class="dbconsole-blocks-drawer" direction="rtl" size="380px"
            :with-header="false" append-to-body>
            <div class="dbconsole-tables">
                <div class="dbconsole-right__head">
                    <span class="dbconsole-title">表</span>
                    <span class="dbconsole-right__count">{{ tableFiltered.length }}</span>
                    <el-button class="dbconsole-right__close" size="small" text @click="tablesDrawer = false">
                        <el-icon>
                            <Close />
                        </el-icon>
                    </el-button>
                </div>
                <el-input v-model="tableKeyword" class="dbconsole-right__search" size="small" clearable
                    placeholder="筛选表名（忽略大小写与下划线）">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <!-- 懒加载：根节点只渲染前 N 张表，滚到底部/点「加载更多」再追加下一批；表的字段在展开时才查 -->
                <div ref="tableListEl" class="dbconsole-tables__list" v-loading="tableLoading" @scroll="onTablesScroll">
                    <el-empty v-if="tableLoaded && tableFiltered.length === 0" description="暂无表" :image-size="60" />
                    <el-tree v-else ref="tableTree" :key="tablesTreeKey" class="dbconsole-tables__tree" :data="tableRows"
                        lazy :load="loadTableNode" :props="tableTreeProps" node-key="id" highlight-current>
                        <template #default="{ data }">
                            <span class="dbtree-node" :class="{ 'is-field': data.leaf }">
                                <span class="dbtree-node__label">{{ data.label }}</span>
                                <span v-if="data.type" class="dbtree-node__type">{{ data.type }}</span>
                                <span v-if="data.comment" class="dbtree-node__comment" :title="data.comment">{{ data.comment }}</span>
                            </span>
                        </template>
                    </el-tree>
                </div>

                <!-- 分批加载提示（还有未显示的表时出现） -->
                <div v-if="tableLoaded && tableFiltered.length > tableRows.length" class="dbconsole-tables__more">
                    <span>已显示 {{ tableRows.length }} / 共 {{ tableFiltered.length }}</span>
                    <el-button size="small" text type="primary" @click="loadMoreTables">加载更多</el-button>
                </div>
            </div>
        </el-drawer>

        <!-- 保存 SQL 块 -->
        <el-dialog class="dbman-dialog" :title="saveForm.mode === 'create' ? '保存 SQL 块' : '编辑 SQL 块'"
            v-model="saveForm.dialog" width="min(520px, 92vw)" append-to-body>
            <el-form label-width="90px">
                <el-form-item label="名称">
                    <el-input v-model="saveForm.name" placeholder="请输入名称" maxlength="100" />
                </el-form-item>
                <el-form-item label="描述">
                    <el-input type="textarea" :rows="3" v-model="saveForm.description" maxlength="200"
                        placeholder="可选，便于以后查阅" />
                </el-form-item>
                <el-form-item label="共享">
                    <div class="dbman-switch-row">
                        <el-switch v-model="saveForm.isShare" />
                        <span class="dbman-tips">开启后，该连接团队成员均可查看并执行</span>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button @click="saveForm.dialog = false">取 消</el-button>
                    <el-button type="primary" :loading="saveForm.saving" @click="doSave">保 存</el-button>
                </div>
            </template>
        </el-dialog>

        <!-- 收藏所选行 -->
        <el-dialog class="dbman-dialog" title="收藏所选行" v-model="favoriteForm.dialog" width="min(520px, 92vw)"
            append-to-body>
            <el-form label-width="90px">
                <el-form-item label="名称">
                    <el-input v-model="favoriteForm.name" placeholder="请输入收藏名称" maxlength="100" />
                </el-form-item>
                <!-- 当次执行的语句（会随收藏一起保存，便于日后对照） -->
                <el-form-item label="执行语句">
                    <div class="dbman-sql-view">{{ favoriteForm.sql || '（未记录）' }}</div>
                </el-form-item>
                <el-form-item label="描述">
                    <el-input type="textarea" :rows="3" v-model="favoriteForm.description" maxlength="200"
                        placeholder="可选，便于以后查阅" />
                </el-form-item>
                <el-form-item label="共享">
                    <div class="dbman-switch-row">
                        <el-switch v-model="favoriteForm.isShare" />
                        <span class="dbman-tips">开启后，该连接团队成员均可查看</span>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button @click="favoriteForm.dialog = false">取 消</el-button>
                    <el-button type="primary" :loading="favoriteForm.saving" @click="doFavorite">保 存</el-button>
                </div>
            </template>
        </el-dialog>

        <!-- 收藏结果（某 SQL 块下的全部结果收藏，按时间倒序）
             时间轴卡片：左侧突出时间，右侧说明与操作，可多张同时展开数据表格以做对比 -->
        <el-dialog class="dbman-dialog dbman-dialog--favlist" :title="`收藏结果（${favListDialog.list.length}）`"
            v-model="favListDialog.dialog" width="min(900px, 94vw)" append-to-body>
            <div class="favx">
                <div class="favx__toolbar">
                    <span class="favx__block">{{ favListDialog.blockName || 'SQL 块' }}</span>
                    <span class="favx__count">{{ favListDialog.list.length }} 组</span>
                    <el-button v-if="favListDialog.list.length" size="small" text type="primary"
                        @click="toggleExpandAll">
                        {{ allExpanded ? '全部收起' : '全部展开' }}
                    </el-button>
                </div>
                <div v-loading="favListDialog.loading" class="favx__list">
                    <el-empty v-if="!favListDialog.loading && favListDialog.list.length === 0"
                        description="暂无结果收藏" :image-size="70" />
                    <div v-for="fav in favListDialog.list" :key="fav.id" class="favx-card"
                        :class="{ 'is-open': isOpen(fav) }">
                        <div class="favx-card__head" @click="toggleExpand(fav)">
                            <!-- 左侧：时间（对比的核心信息） -->
                            <div class="favx-card__time">
                                <span class="favx-card__date">{{ fav.date }}</span>
                                <span class="favx-card__clock">{{ fav.time }}</span>
                            </div>
                            <!-- 中间：名称 + 说明 -->
                            <div class="favx-card__main">
                                <div class="favx-card__name">
                                    <span class="favx-card__title" :title="fav.favoriteName">{{ fav.favoriteName }}</span>
                                    <el-tag v-if="fav.isShare" size="small" type="success" effect="light">共享中</el-tag>
                                    <span class="favx-card__rows">{{ fav.rowCount }} 行</span>
                                    <span v-if="!fav.ownedByMe" class="favx-card__other">他人</span>
                                </div>
                                <div class="favx-card__desc" :title="fav.description">
                                    {{ fav.description || '无说明' }}
                                </div>
                            </div>
                            <!-- 右侧：操作 + 展开箭头（按钮阻止冒泡，避免误触展开） -->
                            <div class="favx-card__ops">
                                <el-button size="small" plain :type="fav.isShare ? 'success' : 'default'"
                                    :disabled="!fav.ownedByMe" @click.stop="toggleFavShare(fav)">
                                    {{ fav.isShare ? '取消共享' : '共享' }}
                                </el-button>
                                <el-button size="small" plain type="danger" :disabled="!fav.ownedByMe"
                                    @click.stop="confirmDeleteFav(fav)">删除</el-button>
                                <el-icon class="favx-card__arrow" :class="{ 'is-open': isOpen(fav) }">
                                    <ArrowRight />
                                </el-icon>
                            </div>
                        </div>
                        <!-- 展开时才渲染表格（懒加载，避免一次挂载过多 vxe-table） -->
                        <el-collapse-transition>
                            <div v-show="isOpen(fav)" class="favx-card__body">
                                <div v-if="fav.sqlText" class="favx-card__sql">
                                    <span class="favx-card__sql-label">执行语句</span>
                                    <pre class="favx-card__sql-text">{{ fav.sqlText }}</pre>
                                </div>
                                <vxe-table v-if="isOpen(fav)" :data="fav._rows" :max-height="320" size="small" border
                                    stripe show-overflow :row-config="{ keyField: '__idx', isHover: true }"
                                    :scroll-y="{ enabled: true, gt: 20 }"
                                    :sort-config="{ trigger: 'cell', orders: ['asc', 'desc', null] }">
                                    <vxe-column type="seq" width="56" title="#" />
                                    <vxe-column v-for="(col, ci) in fav.columns" :key="ci" :field="'c' + ci"
                                        :title="col.name" min-width="140" :sortable="true">
                                        <template #default="{ row }">
                                            <span v-if="row['c' + ci] === null" class="dbconsole-null">NULL</span>
                                            <span v-else>{{ row['c' + ci] }}</span>
                                        </template>
                                    </vxe-column>
                                </vxe-table>
                            </div>
                        </el-collapse-transition>
                    </div>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import debounce from 'lodash/debounce';
import { format as formatSql } from 'sql-formatter';
import request from '../../../utils/request.js';
import { splitSql } from '../../../utils/sqlSplitter.js';
import DbResultPanel from './DbResultPanel.vue';
import SqlEditor from './SqlEditor.vue';

let blockSeq = 0;
// 单次执行最多允许的语句条数（防止粘贴超长脚本）
const MAX_STATEMENTS = 50;
// 空表名/空列名/空注释占位（避免每次渲染新建对象、也避免被 Vue 深层响应式包装）
const EMPTY_TABLES = markRaw([]);
const EMPTY_COLUMNS = markRaw({});
const EMPTY_COMMENTS = markRaw({});

export default {
    name: 'DbConsole',
    components: { DbResultPanel, SqlEditor },
    props: {
        // 当前连接的配置（每个连接对应一个页签、一个控制台实例）
        conn: { type: Object, default: () => ({}) }
    },
    emits: ['changed'],
    data() {
        return {
            // 「已保存 SQL 块」小抽屉是否打开（点右上角标记打开）
            blocksDrawer: false,
            // 「表」浏览器小抽屉
            tablesDrawer: false,
            tableList: [],
            tableLoading: false,
            // 是否已尝试加载过表名（用于区分「加载中」与「确实没有表」）
            tableLoaded: false,
            tableKeyword: '',
            // 根节点分批：已加载到第几页（每页 tablePageSize 张表），避免一次渲染上万节点
            tablePage: 1,
            tablePageSize: 200,
            // 树的重挂载 key：每次打开（或切换数据库后）自增，触发根节点重新加载
            tablesTreeKey: 0,
            // 用户在块里选择的数据库（用于「表」浏览器按该库查询；空则回退到块的库/连接默认库）
            tablesDb: '',
            tableTreeProps: { label: 'label', children: 'children', isLeaf: 'leaf' },
            databases: [],
            dbLoading: false,
            blocks: [],
            // ---- 右侧「SQL 块」面板：服务端分页 ----
            // 是否带上别人共享的块（默认关闭，只看自己创建的）
            showShared: false,
            // 按名称筛选
            blockKeyword: '',
            rightBlocks: [],
            blockLoading: false,
            blockPage: { current: 1, size: 100, total: 0 },
            // 打开抽屉时重置筛选项会触发 watch，用这个标记避免重复请求
            initializing: false,
            saveForm: { mode: 'create', dialog: false, block: null, name: '', description: '', isShare: false, saving: false },
            favoriteForm: { dialog: false, block: null, rows: [], columns: [], sql: '', name: '', description: '', isShare: false, saving: false },
            // 某 SQL 块下的收藏结果弹框（active = 已展开的面板 id，可多个）
            favListDialog: { dialog: false, blockId: '', blockName: '', list: [], active: [], loading: false },
            // 表名缓存：key = connId|库名（空串代表连接默认库），value = 表名数组（markRaw，避免深层响应式）
            tableCache: {},
            // 表注释缓存：key = connId|库名，value = { 表名大写: 注释 }（markRaw，供补全列表展示）
            tableCommentsCache: {},
            // 正在请求中的表名 key（避免重复请求）
            tablePending: {},
            // 列名缓存（按需拉取）：key = connId|库名，value = { 表名: [列名] }（markRaw）
            columnCache: {},
            // 已请求过列名的表（key = connId|库名::表名大写），避免重复请求
            columnRequested: {}
        };
    },
    computed: {
        // SQL 编辑器的方言（CodeMirror mode）：按连接类型选择，高亮/补全更准
        sqlMode() {
            return this.conn && this.conn.dbType === 'mysql' ? 'text/x-mysql' : 'text/x-pgsql';
        },
        // 当前编辑块用到的库集合（空串代表连接默认库）——用于按需拉取表结构供补全
        usedDatabases() {
            return Array.from(new Set(this.blocks.map(b => b.database || '')));
        },
        // 「表」浏览器查询的目标库：优先取用户在块里选择的库，其次取第一个块的库，最后回退连接默认库
        tablesTargetDb() {
            return this.tablesDb
                || (this.blocks[0] && this.blocks[0].database)
                || this.conn.defaultDb
                || '';
        },
        // 「表」浏览器的表名筛选：忽略大小写与下划线（容错「忘记输下划线」）
        tableFiltered() {
            const kw = String(this.tableKeyword || '').toLowerCase().replace(/_/g, '');
            if (!kw) return this.tableList;
            return this.tableList.filter((t) => {
                const name = (t && typeof t === 'object') ? t.name : t;
                return String(name || '').toLowerCase().replace(/_/g, '').indexOf(kw) !== -1;
            });
        },
        // 分批展示：只把前 tablePage 页的表作为树的根节点（避免一次渲染上万节点）
        tableRows() {
            const limit = this.tablePage * this.tablePageSize;
            return this.tableFiltered.slice(0, limit).map((t) => {
                const name = (t && typeof t === 'object') ? t.name : t;
                return {
                    id: 't:' + name,
                    label: name,
                    comment: (t && typeof t === 'object' && t.comment) ? t.comment : ''
                };
            });
        },
        // 「收藏结果」是否已全部展开
        allExpanded() {
            const list = this.favListDialog.list;
            return list.length > 0 && list.every(f => this.favListDialog.active.indexOf(f.id) > -1);
        }
    },
    watch: {
        // 编辑块用到的库变化（新增块、切换某块的库）→ 按需拉取对应库的表名（供补全）
        usedDatabases: {
            immediate: true,
            handler(list) {
                list.forEach((db) => this.ensureTables(db));
            }
        },
        // 「查看共享」切换：回到第 1 页重新拉
        showShared() {
            if (this.initializing) return;
            this.reloadRightBlocks();
        },
        // 「按名称筛选」：防抖后回到第 1 页重新拉
        blockKeyword() {
            if (this.initializing) return;
            this.debouncedReloadRightBlocks();
        },
        // 「表」浏览器：筛选表名 → 回到第 1 页（树的根节点由 tableRows 计算属性驱动重建）
        tableKeyword() {
            this.tablePage = 1;
        }
    },
    created() {
        // 输入框防抖：拉到 700ms，等用户把一个词敲完再请求，减少服务端压力
        this.debouncedReloadRightBlocks = debounce(() => {
            this.reloadRightBlocks();
        }, 700);
    },
    mounted() {
        // 每个连接一个控制台实例：挂载即初始化（新增一个空块 + 拉库列表 + 拉已保存的 SQL 块）
        this.initConsole();
        // 页签刚挂载时布局可能未稳定，补测 CodeMirror，避免行号槽宽度被量成 0
        this.refreshAllEditors();
        this.$nextTick(() => this.refreshAllEditors());
    },
    methods: {
        initConsole() {
            this.blocks = [];
            // 每次打开都回到默认：不显示他人共享的块、不筛选
            this.initializing = true;
            this.showShared = false;
            this.blockKeyword = '';
            this.blockPage.current = 1;
            this.rightBlocks = [];
            this.addBlock();
            this.loadDatabases();
            this.loadRightBlocks().finally(() => {
                this.initializing = false;
            });
        },
        // 右侧 SQL 块：服务端分页 + 名称筛选
        loadRightBlocks() {
            if (!this.conn.id) return Promise.resolve();
            this.blockLoading = true;
            return request({
                url: '/dbManSqlBlock/pageList',
                method: 'post',
                data: {
                    connId: this.conn.id,
                    keyword: this.blockKeyword,
                    includeShared: this.showShared,
                    current: this.blockPage.current,
                    size: this.blockPage.size
                }
            }).then(res => {
                if (res.code == 0) {
                    this.rightBlocks = res.data || [];
                    this.blockPage.total = res.total || 0;
                }
            }).finally(() => {
                this.blockLoading = false;
            });
        },
        reloadRightBlocks() {
            this.blockPage.current = 1;
            return this.loadRightBlocks();
        },
        onBlockPageChange(page) {
            this.blockPage.current = page;
            this.loadRightBlocks();
        },
        // 取某块对应的 SQL 编辑器实例（v-for 里用「每块唯一名称」的字符串 ref，
        // 避免函数式 ref 每次渲染重建导致的解绑/重绑）
        editorFor(block) {
            if (!block) return null;
            const r = this.$refs['sqlEditor-' + block.uid];
            return Array.isArray(r) ? (r[0] || null) : (r || null);
        },
        // 刷新所有 SQL 编辑器（修正行号槽/滚动尺寸的测量偏差）
        refreshAllEditors() {
            this.blocks.forEach((b) => {
                const ed = this.editorFor(b);
                if (ed && typeof ed.refresh === 'function') ed.refresh();
            });
        },
        // 卡片进入/移出动画结束：布局已稳定，统一重测编辑器（详见模板里的说明）
        onBlockAnimDone() {
            this.$nextTick(() => this.refreshAllEditors());
        },
        loadDatabases() {
            this.dbLoading = true;
            return request({
                url: '/dbManExec/databases',
                method: 'post',
                data: { connId: this.conn.id }
            }).then(res => {
                if (res.code == 0) {
                    this.databases = res.meta.databases || [];
                }
            }).finally(() => {
                this.dbLoading = false;
            });
        },
        // ---------- 左侧：SQL 块 ----------
        addBlock(seed) {
            const block = {
                uid: 'blk-' + (++blockSeq),
                id: (seed && seed.id) || '',
                name: (seed && seed.name) || '',
                description: (seed && seed.description) || '',
                isShare: (seed && seed.isShare) || false,
                sqlText: (seed && seed.sqlText) || '',
                // 该块要执行的数据库：已保存的块用其记录值，否则取连接默认库
                databaseName: (seed && seed.databaseName) || '',
                database: (seed && (seed.database || seed.databaseName)) || this.conn.defaultDb || '',
                loading: false,
                // 多语句执行：每条语句一个结果（含各自的 result/error/loading/summary）
                results: [],
                // 多语句时当前选中的结果 tab（存语句 id）
                activeResult: '',
                // 新加入标记：短暂高亮，便于用户定位新增/载入的块
                _fresh: false
            };
            // 新增的块放到最上方
            this.blocks.unshift(block);
            return block;
        },
        // 高亮新加入的块，并确保滚动到可见位置
        highlightNew(block) {
            block._fresh = true;
            this.$nextTick(() => {
                const list = this.$refs.leftList;
                if (list) list.scrollTo({ top: 0, behavior: 'smooth' });
            });
            setTimeout(() => {
                block._fresh = false;
            }, 2400);
            return block;
        },
        removeBlock(block) {
            const idx = this.blocks.indexOf(block);
            if (idx > -1) this.blocks.splice(idx, 1);
        },
        copySql(block) {
            const text = block.sqlText || '';
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    ElMessage({ message: '复制成功', type: 'success', duration: 1000 });
                });
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                ElMessage({ message: '复制成功', type: 'success', duration: 1000 });
            }
        },
        // 格式化当前块的 SQL（按连接方言），失败时提示并保留原文
        formatBlock(block) {
            const sql = (block.sqlText || '').trim();
            if (!sql) {
                ElMessage({ message: '请输入 SQL', type: 'warning', duration: 2000 });
                return;
            }
            try {
                block.sqlText = formatSql(sql, {
                    language: this.conn && this.conn.dbType === 'mysql' ? 'mysql' : 'postgresql',
                    keywordCase: 'upper'
                });
            } catch (e) {
                ElMessage({ message: '格式化失败：' + (e && e.message ? e.message : '语法无法解析'), type: 'error', duration: 3000 });
            }
        },
        // 单条语句结果的摘要文案（结果 tab 标题用）
        summarizeResult(result) {
            if (!result) return '';
            if (result.type === 'QUERY') return (result.truncated ? '≥' : '') + result.rowCount + ' 行';
            if (result.type === 'UPDATE') return '影响 ' + result.affectedRows + ' 行';
            return '已执行';
        },
        // 遇错即停：把后续语句标记为「未执行」
        markSkipped(results, fromIndex) {
            for (let i = fromIndex; i < results.length; i++) {
                results[i].status = 'skipped';
                results[i].summary = '未执行';
            }
        },
        // 执行全部：顺序执行编辑器里的所有语句
        async run(block) {
            await this.executeSql(block, block.sqlText);
        },
        // 生产防护锁确认：需要写操作时请用户输入连接名；返回确认词，取消返回 null，无需确认返回 ''
        async promptProdLock() {
            if (!this.conn.prodLock) return '';
            try {
                const result = await ElMessageBox.prompt(
                    '该连接已开启生产防护锁，请输入连接名确认（写操作需要）', '生产防护锁确认',
                    { confirmButtonText: '确定', cancelButtonText: '取消', inputPlaceholder: this.conn.name });
                return result.value;
            } catch (e) {
                return null;
            }
        },
        // 提交结果表格的改动（由结果面板回调）：生产防护锁确认 → 调接口（单事务）→ 成功后只刷新**当前语句**的结果
        async commitResultChanges(block, index, payload) {
            const confirmText = await this.promptProdLock();
            if (confirmText === null) return false;
            try {
                const res = await request({
                    url: '/dbManExec/applyChanges',
                    method: 'post',
                    // silent：失败不弹全局提示，这里就地给出更明确的文案
                    silent: true,
                    data: {
                        token: this.$store.state.user.token,
                        connId: this.conn.id,
                        // 目标库优先用结果表所属库（edit.database），退回块的库
                        database: payload.database || block.database || '',
                        schema: payload.schema || '',
                        table: payload.table,
                        confirmText,
                        changes: payload.changes
                    }
                });
                ElMessage({ message: `提交成功，影响 ${(res.meta && res.meta.affected) || 0} 行`, type: 'success', duration: 2500 });
                // 只重跑当前 tab 对应的那条语句（不整块重跑）
                await this.refreshStatement(block, index);
                return true;
            } catch (e) {
                const reason = (e && e.message) ? String(e.message) : '';
                ElMessage({ message: reason || '提交失败', type: 'error', duration: 4000 });
                return false;
            }
        },
        // 只重跑某一条语句，就地替换该结果（提交后刷新当前 tab 用）
        async refreshStatement(block, index) {
            const st = block.results && block.results[index];
            if (!st) return;
            st.loading = true;
            try {
                const res = await request({
                    url: '/dbManExec/query',
                    method: 'post',
                    silent: true,
                    data: {
                        connId: this.conn.id,
                        sql: st.sql,
                        sqlBlockId: block.id || '',
                        database: block.database || '',
                        maxRows: 200,
                        timeoutSec: 30,
                        confirmText: ''
                    }
                });
                st.result = markRaw(res.meta.result);
                st.error = '';
                st.status = 'done';
                st.summary = this.summarizeResult(res.meta.result);
            } catch (e) {
                const reason = (e && e.message) ? String(e.message) : '';
                st.error = reason
                    ? (reason.indexOf('执行失败') === 0 ? reason : '执行失败：' + reason)
                    : '执行失败';
                st.status = 'error';
                st.summary = '错误';
            } finally {
                st.loading = false;
            }
        },
        // Ctrl/Cmd+Enter 快捷键入口：有选中文本则执行选中，否则执行全部
        async onRun(block, selected) {
            const text = (selected || '').trim();
            await this.executeSql(block, text || block.sqlText);
        },
        // 执行选中：只执行编辑器中选中的文本（同样支持多条语句拆分）
        async runSelected(block) {
            const ed = this.editorFor(block);
            const selected = ed && typeof ed.getSelectedText === 'function' ? ed.getSelectedText() : '';
            if (!selected || !selected.trim()) {
                ElMessage({ message: '请先在编辑器中选中要执行的 SQL', type: 'warning', duration: 2000 });
                return;
            }
            await this.executeSql(block, selected);
        },
        // 执行给定 SQL 文本（可能是多条语句）：校验 → 拆分 → 生产防护确认 → 串行执行
        async executeSql(block, sqlText) {
            const sql = (sqlText || '').trim();
            if (!sql) {
                ElMessage({ message: '请输入 SQL', type: 'warning', duration: 2000 });
                return;
            }
            // 前端拆分多语句（正确处理字符串/注释/美元引用里的分号）
            const allSql = splitSql(sql, { backslashEscapes: this.conn && this.conn.dbType === 'mysql' });
            if (allSql.length === 0) {
                ElMessage({ message: '未识别到可执行的 SQL', type: 'warning', duration: 2000 });
                return;
            }
            if (allSql.length > MAX_STATEMENTS) {
                ElMessage({ message: `一次最多执行 ${MAX_STATEMENTS} 条语句（当前 ${allSql.length} 条）`, type: 'warning', duration: 3000 });
                return;
            }
            // 生产防护锁：整批确认一次
            const confirmText = await this.promptProdLock();
            if (confirmText === null) return;
            // 复用上一次「同位置且同 SQL」的结果：执行中保留旧表格 + 叠加载入遮罩，返回后一次性替换
            const prev = block.results || [];
            const prevActive = block.activeResult;
            block.results = allSql.map((s, i) => {
                const old = prev[i] && prev[i].sql === s ? prev[i] : null;
                return {
                    id: 'st-' + i,
                    sql: s,
                    result: old ? old.result : null,
                    error: '',
                    loading: false,
                    status: 'pending',
                    summary: old ? old.summary : ''
                };
            });
            block.activeResult = block.results.some(r => r.id === prevActive) ? prevActive : block.results[0].id;
            block.loading = true;

            // 串行执行：顺序敏感（DDL/DML），且能限制瞬时负载
            for (let i = 0; i < block.results.length; i++) {
                const st = block.results[i];
                st.status = 'running';
                st.loading = true;
                try {
                    const res = await request({
                        url: '/dbManExec/query',
                        method: 'post',
                        // silent：执行失败不弹全局提示，由下方 catch 把失败原因就地展示在结果区
                        silent: true,
                        data: {
                            connId: this.conn.id,
                            sql: st.sql,
                            sqlBlockId: block.id || '',
                            database: block.database || '',
                            maxRows: 200,
                            timeoutSec: 30,
                            confirmText
                        }
                    });
                    // 走到这里说明 code == 0（非 0 已被响应拦截器 reject，进入 catch）
                    // markRaw：大结果集不做深层响应式，避免拖慢渲染
                    st.result = markRaw(res.meta.result);
                    st.status = 'done';
                    st.summary = this.summarizeResult(res.meta.result);
                } catch (e) {
                    // 执行失败：把失败原因就地展示（后端消息本身多以「执行失败：」开头，避免重复前缀）
                    const reason = (e && e.message) ? String(e.message) : '';
                    st.error = reason
                        ? (reason.indexOf('执行失败') === 0 ? reason : '执行失败：' + reason)
                        : '执行失败';
                    st.status = 'error';
                    st.summary = '错误';
                    this.markSkipped(block.results, i + 1);
                    break;
                } finally {
                    st.loading = false;
                }
            }
            block.loading = false;
        },
        openSave(block) {
            this.saveForm.mode = block.id ? 'edit' : 'create';
            this.saveForm.block = block;
            this.saveForm.name = block.name || '';
            this.saveForm.description = block.description || '';
            this.saveForm.isShare = !!block.isShare;
            this.saveForm.dialog = true;
        },
        doSave() {
            const form = this.saveForm;
            if (!form.name) {
                ElMessage({ message: '请填写名称', type: 'warning', duration: 2000 });
                return;
            }
            const block = form.block;
            if (!block.sqlText || !block.sqlText.trim()) {
                ElMessage({ message: 'SQL 不能为空', type: 'warning', duration: 2000 });
                return;
            }
            form.saving = true;
            request({
                url: '/dbManSqlBlock/save',
                method: 'post',
                data: {
                    token: this.$store.state.user.token,
                    id: block.id || '',
                    connId: this.conn.id,
                    name: form.name,
                    description: form.description,
                    sqlText: block.sqlText,
                    // 一并记录该块要执行的数据库，便于他人识别在哪个库执行
                    // （右侧已保存的块没有 block.database，用其 databaseName 兜底，避免被清空）
                    databaseName: block.database || block.databaseName || '',
                    isShare: form.isShare,
                    orderIndex: 0
                }
            }).then(res => {
                if (res.code == 0) {
                    const saved = res.meta.dbSqlBlock;
                    block.id = saved.id;
                    block.name = saved.name;
                    block.description = saved.description;
                    block.isShare = saved.isShare;
                    block.databaseName = saved.databaseName || block.database || block.databaseName;
                    ElMessage({ message: '保存成功', type: 'success', duration: 2000 });
                    form.dialog = false;
                    // 刷新右侧列表（当前页）
                    this.loadRightBlocks();
                    this.$emit('changed');
                } else {
                    ElMessage({ message: res.msg || '保存失败', type: 'error', duration: 3000 });
                }
            }).finally(() => {
                form.saving = false;
            });
        },
        // ---------- 右侧：SQL 块（服务端分页 + 名称筛选） ----------
        // 操作菜单分发
        onBlockCommand(cmd, sb) {
            if (cmd === 'favs') return this.openFavList(sb);
            if (cmd === 'edit') return this.openEditRight(sb);
            if (cmd === 'share') return this.toggleShare(sb);
            if (cmd === 'delete') return this.confirmDeleteBlock(sb);
        },
        // 打开某 SQL 块的结果收藏（按时间倒序）；列表已含 columns/rows 快照，预计算成表格行
        openFavList(sb) {
            this.favListDialog.blockId = sb.id;
            this.favListDialog.blockName = sb.name;
            this.favListDialog.list = [];
            this.favListDialog.active = [];
            this.favListDialog.loading = true;
            this.favListDialog.dialog = true;
            request({
                url: '/dbManRowFavorite/list',
                method: 'post',
                data: { sqlBlockId: sb.id, current: 1, size: 100 }
            }).then(res => {
                if (res.code == 0) {
                    // 只保留响应式需要的轻量字段；columns/rows 快照用 markRaw，避免深层响应式开销。
                    // 这样 fav.isShare 仍可响应式切换（共享/取消共享）。
                    this.favListDialog.list = (res.data || []).map(fav => {
                        const raw = fav.createTime ? String(fav.createTime).replace('T', ' ') : '';
                        const seg = raw.split(' ');
                        return {
                            id: fav.id,
                            favoriteName: fav.favoriteName,
                            description: fav.description,
                            sqlText: fav.sqlText || '',
                            isShare: !!fav.isShare,
                            rowCount: fav.rowCount,
                            date: seg[0] || '',
                            time: (seg[1] || '').slice(0, 8),
                            ownedByMe: !!fav.ownedByMe,
                            columns: markRaw(fav.columns || []),
                            _rows: markRaw((fav.rows || []).map((row, ri) => {
                                const obj = { __idx: ri };
                                row.forEach((cell, ci) => {
                                    obj['c' + ci] = cell;
                                });
                                return obj;
                            }))
                        };
                    });
                }
            }).finally(() => {
                this.favListDialog.loading = false;
            });
        },
        // 某条收藏是否已展开
        isOpen(fav) {
            return this.favListDialog.active.indexOf(fav.id) > -1;
        },
        toggleExpand(fav) {
            const i = this.favListDialog.active.indexOf(fav.id);
            if (i > -1) this.favListDialog.active.splice(i, 1);
            else this.favListDialog.active.push(fav.id);
        },
        // 全部展开 / 全部收起
        toggleExpandAll() {
            this.favListDialog.active = this.allExpanded
                ? []
                : this.favListDialog.list.map(f => f.id);
        },
        // 共享 / 取消共享某条收藏结果
        toggleFavShare(fav) {
            const next = !fav.isShare;
            request({
                url: '/dbManRowFavorite/share',
                method: 'post',
                data: { id: fav.id, isShare: next }
            }).then(res => {
                if (res.code == 0) {
                    fav.isShare = next;
                    ElMessage({ message: next ? '已共享' : '已取消共享', type: 'success', duration: 1500 });
                } else {
                    ElMessage({ message: res.msg || '操作失败', type: 'error', duration: 3000 });
                }
            });
        },
        // 删除某条收藏结果（不可恢复）
        confirmDeleteFav(fav) {
            ElMessageBox.confirm(`删除后不可恢复，确定删除收藏「${fav.favoriteName}」吗？`, '删除确认', {
                confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning'
            }).then(() => {
                request({
                    url: '/dbManRowFavorite/delete',
                    method: 'post',
                    data: { id: fav.id }
                }).then(res => {
                    if (res.code == 0) {
                        ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                        const idx = this.favListDialog.list.findIndex(f => f.id === fav.id);
                        if (idx > -1) this.favListDialog.list.splice(idx, 1);
                        this.favListDialog.active = this.favListDialog.active.filter(id => id !== fav.id);
                        // 同步右侧菜单里的「收藏结果（n）」
                        const sb = this.rightBlocks.find(b => b.id === this.favListDialog.blockId);
                        if (sb) sb.favoriteCount = Math.max(0, (sb.favoriteCount || 0) - 1);
                    }
                });
            }).catch(() => { });
        },
        // 编辑右侧已保存的 SQL 块（复用保存弹窗，仅创建者可改）
        openEditRight(sb) {
            if (!sb.ownedByMe) {
                ElMessage({ message: '只能编辑自己创建的 SQL 块', type: 'warning', duration: 2000 });
                return;
            }
            this.openSave(sb);
        },
        // 共享 / 取消共享
        toggleShare(sb) {
            const next = !sb.isShare;
            request({
                url: '/dbManSqlBlock/share',
                method: 'post',
                data: { id: sb.id, isShare: next }
            }).then(res => {
                if (res.code == 0) {
                    sb.isShare = next;
                    ElMessage({ message: next ? '已共享' : '已取消共享', type: 'success', duration: 1500 });
                    this.$emit('changed');
                } else {
                    ElMessage({ message: res.msg || '操作失败', type: 'error', duration: 3000 });
                }
            });
        },
        // 删除 SQL 块（不可恢复，连带删除其结果收藏快照）
        confirmDeleteBlock(sb) {
            ElMessageBox.confirm(
                '删除后不可恢复，该 SQL 块下收藏的结果快照也会被同步删除。确定删除吗？',
                '删除确认',
                { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
            ).then(() => {
                request({
                    url: '/dbManSqlBlock/delete',
                    method: 'post',
                    data: { id: sb.id }
                }).then(res => {
                    if (res.code == 0) {
                        ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                        const idx = this.rightBlocks.findIndex(x => x.id === sb.id);
                        if (idx > -1) this.rightBlocks.splice(idx, 1);
                        this.blockPage.total = Math.max(0, this.blockPage.total - 1);
                        // 当前页被删空且不是第 1 页时，回退一页
                        if (this.rightBlocks.length === 0 && this.blockPage.current > 1) {
                            this.blockPage.current -= 1;
                        }
                        this.loadRightBlocks();
                        this.$emit('changed');
                    }
                });
            }).catch(() => { });
        },
        loadToEditor(sb) {
            const seed = sb.ownedByMe
                ? { id: sb.id, name: sb.name, description: sb.description, isShare: sb.isShare, sqlText: sb.sqlText, databaseName: sb.databaseName }
                : { name: sb.name + '（副本）', description: sb.description, sqlText: sb.sqlText, databaseName: sb.databaseName };
            this.highlightNew(this.addBlock(seed));
            ElMessage({ message: '已载入到左侧编辑区', type: 'success', duration: 1500 });
        },
        // ---------- 表名 / 列名（SQL 补全用） ----------
        // 按「连接 + 库」拉取表名并缓存；同一 key 只请求一次
        ensureTables(database) {
            if (!this.conn || !this.conn.id) return;
            const db = database || '';
            const key = this.conn.id + '|' + db;
            if (this.tableCache[key] || this.tablePending[key]) return;
            this.tablePending[key] = true;
            request({
                url: '/dbManExec/tables',
                method: 'post',
                data: { connId: this.conn.id, database: db }
            }).then(res => {
                if (res.code == 0) {
                    // 注意：okData(key, value) 是放进 meta（与 dbManExec/databases 的 res.meta.databases 一致）
                    // 后端返回 [{name, comment}]：名字用于补全，注释用于补全列表展示
                    // markRaw：只用于补全，不需要响应式
                    const list = (res.meta && res.meta.tables) || [];
                    const arr = Array.isArray(list) ? list : [];
                    const names = [];
                    const comments = {};
                    arr.forEach((t) => {
                        const name = (t && typeof t === 'object') ? t.name : t;
                        if (!name) return;
                        names.push(name);
                        if (t && typeof t === 'object' && t.comment) {
                            comments[String(name).toUpperCase()] = t.comment;
                        }
                    });
                    this.tableCache[key] = markRaw(names);
                    this.tableCommentsCache[key] = markRaw(comments);
                    if (!this.columnCache[key]) this.columnCache[key] = EMPTY_COLUMNS;
                }
            }).catch(() => {
                // 后端对「取不到表名」已返回空列表、不报错；这里只是兜底
            }).finally(() => {
                this.tablePending[key] = false;
            });
        },
        // 编辑器告知：当前语句用到了这些表 → 按需拉取这些表的列（名 + 注释；已拉过的不再请求）
        onNeedColumns(database, tables) {
            if (!this.conn || !this.conn.id || !tables || !tables.length) return;
            const db = database || '';
            const key = this.conn.id + '|' + db;
            if (!this.columnCache[key]) this.columnCache[key] = EMPTY_COLUMNS;
            const cache = this.columnCache[key] || {};
            const existing = new Set(Object.keys(cache).map(k => k.toUpperCase()));
            const missing = [];
            tables.forEach(t => {
                if (!t) return;
                const upper = String(t).toUpperCase();
                const pendKey = key + '::' + upper;
                if (existing.has(upper) || this.columnRequested[pendKey]) return;
                this.columnRequested[pendKey] = true;
                missing.push(t);
            });
            if (!missing.length) return;
            // 逐表拉取（与「表」浏览器同一个接口，返回 name + type + comment）
            missing.forEach((table) => {
                request({
                    url: '/dbManExec/tableColumns',
                    method: 'post',
                    silent: true,
                    data: { connId: this.conn.id, database: db, table }
                }).then(res => {
                    if (res.code != 0) return;
                    const cols = (res.meta && res.meta.columns) || [];
                    const list = cols.map(c => ({ name: c.name, type: c.type || '', comment: c.comment || '' }));
                    // 合并进该库的列缓存（新对象 → 触发编辑器 props 更新）
                    this.columnCache[key] = markRaw(Object.assign({}, this.columnCache[key] || {}, { [table]: list }));
                }).catch(() => {
                    // 失败则清掉标记，允许下次重试
                    delete this.columnRequested[key + '::' + String(table).toUpperCase()];
                });
            });
        },
        // 取某库的表名（补全用）；未拉到或拉取失败时返回空数组
        tablesFor(database) {
            return this.tableCache[this.conn.id + '|' + (database || '')] || EMPTY_TABLES;
        },
        // 取某库的表注释（补全列表展示用）；未拉到或拉取失败时返回空对象
        tableCommentsFor(database) {
            return this.tableCommentsCache[this.conn.id + '|' + (database || '')] || EMPTY_COMMENTS;
        },
        // 取某库已按需拉到的列（补全用）；未拉到或拉取失败时返回空对象
        columnsFor(database) {
            return this.columnCache[this.conn.id + '|' + (database || '')] || EMPTY_COLUMNS;
        },
        // ---------- 「表」浏览器（树形：表 → 字段；根节点分批加载、字段展开才加载） ----------
        // 打开「表」抽屉：每次都按当前目标库重新查询（切换数据库后重开即可刷新）
        openTables() {
            this.tablesDrawer = true;
            this.resetTablesTree();
            this.fetchTableList();
        },
        // 重置「表」树：清空表名/筛选词/分页，并让树重挂载（清掉已展开的字段）
        resetTablesTree() {
            this.tableList = [];
            this.tableLoaded = false;
            this.tableKeyword = '';
            this.tablePage = 1;
            this.tablesTreeKey += 1;
        },
        // 拉取目标库的表名（名 + 注释）；失败按空处理
        fetchTableList() {
            this.tableLoading = true;
            return request({
                url: '/dbManExec/tables',
                method: 'post',
                silent: true,
                data: { connId: this.conn.id, database: this.tablesTargetDb }
            }).then(res => {
                // 后端返回 [{name, comment}]：作为树的根节点数据（注释一并展示）
                const list = (res.meta && res.meta.tables) || [];
                this.tableList = Array.isArray(list) ? list : [];
                return this.tableList;
            }).catch(() => {
                this.tableList = [];
                return [];
            }).finally(() => {
                this.tableLoading = false;
                this.tableLoaded = true;
            });
        },
        // 追加下一批表（滚到底部或点「加载更多」时触发）
        loadMoreTables() {
            if (this.tableRows.length >= this.tableFiltered.length) return;
            const list = this.$refs.tableListEl;
            const keep = list ? list.scrollTop : 0;
            this.tablePage += 1;
            // 根节点重建后恢复滚动位置（内容是往后追加的，原位置内容不变）
            this.$nextTick(() => {
                if (list && list.scrollTop !== keep) list.scrollTop = keep;
            });
        },
        // 列表滚动到接近底部时自动追加下一批
        onTablesScroll(e) {
            const el = e.target;
            if (!el) return;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
                this.loadMoreTables();
            }
        },
        // el-tree 懒加载：展开某表时才拉该表的字段（名 + 类型 + 注释）
        loadTableNode(node, resolve) {
            if (node.level === 0) {
                resolve([]);
                return;
            }
            const table = node.data.label;
            request({
                url: '/dbManExec/tableColumns',
                method: 'post',
                silent: true,
                data: { connId: this.conn.id, database: this.tablesTargetDb, table }
            }).then(res => {
                const cols = (res.meta && res.meta.columns) || [];
                resolve(cols.map((c) => ({
                    id: 'f:' + table + '.' + c.name,
                    label: c.name,
                    type: c.type || '',
                    comment: c.comment || '',
                    leaf: true
                })));
            }).catch(() => resolve([]));
        },
        // ---------- 收藏所选行 ----------
        openFavorite(block, rows, result, sql) {
            if (!block.id) {
                ElMessage({ message: '请先保存该 SQL 块，再进行结果行收藏', type: 'warning', duration: 3000 });
                return;
            }
            if (!rows || rows.length === 0) {
                ElMessage({ message: '请先勾选要收藏的行', type: 'warning', duration: 2000 });
                return;
            }
            if (rows.length > 100) {
                ElMessage({ message: '一次最多收藏 100 行', type: 'warning', duration: 2000 });
                return;
            }
            this.favoriteForm.block = block;
            this.favoriteForm.rows = rows;
            this.favoriteForm.columns = (result && result.columns) || [];
            // 记录当次执行的那条语句（多语句时是当前结果 tab 对应的那条）
            this.favoriteForm.sql = sql || block.sqlText || '';
            this.favoriteForm.name = (block.name || '结果') + '-结果';
            this.favoriteForm.description = '';
            this.favoriteForm.isShare = false;
            this.favoriteForm.dialog = true;
        },
        doFavorite() {
            const form = this.favoriteForm;
            if (!form.name) {
                ElMessage({ message: '请填写名称', type: 'warning', duration: 2000 });
                return;
            }
            const block = form.block;
            const rows = form.rows || [];
            form.saving = true;
            request({
                url: '/dbManRowFavorite/save',
                method: 'post',
                data: {
                    sqlBlockId: block.id,
                    favoriteName: form.name,
                    description: form.description,
                    sqlText: form.sql || '',
                    columns: form.columns || [],
                    rows,
                    isShare: form.isShare
                }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '已收藏 ' + rows.length + ' 行', type: 'success', duration: 2000 });
                    form.dialog = false;
                    // 更新右侧该块的收藏数量（菜单里「收藏结果（n）」的 n）
                    const sb = this.rightBlocks.find(x => x.id === block.id);
                    if (sb) {
                        sb.favoriteCount = (sb.favoriteCount || 0) + 1;
                    }
                    // 若正打开着该块的收藏结果弹框，刷新列表
                    if (this.favListDialog.dialog && this.favListDialog.blockName === block.name) {
                        this.openFavList({ id: block.id, name: block.name });
                    }
                } else {
                    ElMessage({ message: res.msg || '收藏失败', type: 'error', duration: 3000 });
                }
            }).finally(() => {
                form.saving = false;
            });
        }
    }
};
</script>

<!-- 控制台面板样式；面板内的「SQL 块」小抽屉 append-to-body，样式需放非 scoped 块 -->
<style>
/* 控制台面板：嵌入右侧页签内，纵向撑满 */
.dbconsole-drawer.dbconsole-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background-color: #ffffff;
}

/* 面板头部：连接信息 + 右上角「SQL 块」标记 */
.dbconsole-panel__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
    min-width: 0;
    padding: 10px 16px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.dbconsole-panel__add {
    margin-left: auto;
}

.dbconsole-panel__blocks-count {
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 999px;
    background-color: #eef3fc;
    color: #2f6fed;
    font-size: 12px;
}

.dbconsole-head__name {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-head__addr {
    font-size: 13px;
    color: #8a94a6;
}

.dbconsole-body {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

.dbconsole-left {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    padding: 12px 16px 18px;
    /* 容器不滚动，由内部的块列表自己滚动，滚动条才不会盖到标题栏 */
    overflow: hidden;
}

/* 块列表区：自己滚动，标题栏固定 */
.dbconsole-drawer .dbconsole-left__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

/* 「已保存 SQL 块」小抽屉：内容区去掉默认内边距，交给 .dbconsole-right 控制 */
.dbconsole-blocks-drawer .el-drawer__body {
    padding: 0;
    overflow: hidden;
}

/* 「已保存 SQL 块」面板（位于右侧小抽屉内） */
.dbconsole-right {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 12px 16px 18px;
    background-color: #fbfcfe;
    overflow: hidden;
}

/* 名称筛选框 */
.dbconsole-right__search {
    flex: 0 0 auto;
    margin-bottom: 8px;
}

/* 列表区：自己滚动，头部/筛选/分页条固定 */
.dbconsole-right__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    /* 给底部悬浮分页条留位置，避免最后一张卡片被挡住 */
    padding-bottom: 52px;
}

/* 底部悬浮胶囊分页条 */
.dbconsole-pager {
    position: absolute;
    left: 50%;
    bottom: 12px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    padding: 2px 10px;
    border: 1px solid #dbe3ef;
    border-radius: 999px;
    background-color: rgba(255, 255, 255, 0.96);
    box-shadow: 0 8px 20px -10px rgba(28, 47, 84, 0.35);
    z-index: 2;
}

.dbconsole-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-right__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.dbconsole-right__count {
    padding: 1px 8px;
    border: 1px solid #d6e0f0;
    border-radius: 999px;
    background-color: #fff;
    color: #2f6fed;
    font-size: 12px;
}

.dbconsole-right__switch {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.dbconsole-right__switch-label {
    font-size: 12px;
    color: #8a94a6;
}

.dbconsole-right__close {
    flex: 0 0 auto;
    padding: 0 4px;
}

/* 「表」浏览器面板（位于右侧小抽屉内） */
.dbconsole-tables {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 12px 16px 18px;
    background-color: #fbfcfe;
    overflow: hidden;
}

.dbconsole-tables__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

/* 分批加载提示条（点击「加载更多」或滚到底部自动追加下一批） */
.dbconsole-tables__more {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #eef1f6;
    color: #8a94a6;
    font-size: 12px;
}

.dbconsole-tables__tree {
    background-color: transparent;
}

/* 树节点：表名 / 字段名 + 右侧类型 */
.dbtree-node {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    font-size: 13px;
}

.dbtree-node__label {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dbtree-node__type {
    flex: 0 0 auto;
    color: #aab4c4;
    font-size: 12px;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
}

/* 注释：紧跟类型/表名之后，占满剩余宽度，超长省略、悬浮查看完整 */
.dbtree-node__comment {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #aab4c4;
    font-size: 12px;
}

/* 字段行：名称用次级色，与表名区分 */
.dbtree-node.is-field .dbtree-node__label {
    color: #5b6a80;
}

.dbconsole-drawer .dbconsole-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 0;
    color: #aab4c4;
    font-size: 13px;
}

.dbconsole-drawer .dbconsole-block {
    margin-bottom: 14px;
    /* 边框加深，便于区分相邻的查询块 */
    border: 1px solid #aebfd6;
    border-left: 3px solid #7d9fd0;
    border-bottom: 3px solid #7d9fd0;
    border-radius: 12px;
    background-color: #fff;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(28, 47, 84, 0.08);
    /* 高亮淡出用 */
    transition: box-shadow 0.6s ease;
    /* 把 vxe-table 内部的 z-index 关进卡片自己的层叠上下文：它内部最高用到 99
       （表格外框 .vxe-table--border-line 是 z-index: 10），不隔离就可能穿透到卡片外。
       isolation: isolate 只建层叠上下文、不改变定位，副作用最小。 */
    isolation: isolate;
}

/* 新增/载入的块：从上方淡入、下移并自上而下展开；下方原有卡片同步下移让位 */
/* 注意：这些类必须带上 .dbconsole-drawer 前缀，否则优先级低于基础规则
   `.dbconsole-drawer .dbconsole-block` 的 transition，动画不会生效（会变成瞬间撑开）。 */
.dbconsole-drawer .dbconsole-block.is-fresh {
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.35), 0 1px 3px rgba(28, 47, 84, 0.08);
}

.dbconsole-drawer .dbconsole-block-anim-move {
    transition: transform 0.55s ease;
}

.dbconsole-drawer .dbconsole-block-anim-enter-active {
    overflow: hidden;
    transition: max-height 0.8s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.6s ease,
        transform 0.6s ease, margin-bottom 0.8s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.dbconsole-drawer .dbconsole-block-anim-enter-from {
    max-height: 0;
    margin-bottom: 0;
    opacity: 0;
    transform: translateY(-24px);
}

.dbconsole-drawer .dbconsole-block-anim-enter-to {
    max-height: 1200px;
}

/* 移除：快一点，向上淡出并收起，增强操作反馈 */
.dbconsole-drawer .dbconsole-block-anim-leave-active {
    overflow: hidden;
    transition: max-height 0.28s ease, opacity 0.22s ease, transform 0.22s ease,
        margin-bottom 0.28s ease;
}

.dbconsole-drawer .dbconsole-block-anim-leave-from {
    max-height: 1200px;
}

.dbconsole-drawer .dbconsole-block-anim-leave-to {
    max-height: 0;
    margin-bottom: 0;
    opacity: 0;
    transform: translateY(-16px);
}

/* 块序号标志（卡片左上角） */
.dbconsole-drawer .dbconsole-block__no {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 999px;
    background-color: #7d9fd0;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
}

.dbconsole-drawer .dbconsole-block__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #dfe7f1;
    background: linear-gradient(180deg, #f3f7fd 0%, #ffffff 100%);
}

.dbconsole-drawer .dbconsole-block__title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.dbconsole-drawer .dbconsole-block__name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-drawer .dbconsole-block__db-select {
    width: 160px;
}

.dbconsole-drawer .dbconsole-block__ops {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.dbconsole-drawer .dbconsole-block__input {
    padding: 8px 12px 0;
}

.dbconsole-drawer .dbconsole-block__result {
    padding: 8px 12px 12px;
}

/* 多语句结果 tab：紧凑样式，标题带序号 + 状态摘要 */
.dbconsole-drawer .dbconsole-block__tabs .el-tabs__header {
    margin: 0 0 8px;
}

.dbconsole-drawer .dbconsole-block__tabs .el-tabs__item {
    height: 30px;
    line-height: 30px;
    padding: 0 10px;
    font-size: 12px;
}

.dbconsole-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.dbconsole-tab__no {
    color: #7d8ba1;
    font-weight: 600;
}

.dbconsole-tab__sum.is-done {
    color: #4c9a6b;
}

.dbconsole-tab__sum.is-error {
    color: #f56c6c;
}

.dbconsole-tab__sum.is-running {
    color: #2f6fed;
}

.dbconsole-tab__sum.is-skipped,
.dbconsole-tab__sum.is-pending {
    color: #aab4c4;
}

.dbconsole-null {
    color: #aab4c4;
    font-style: italic;
}

.dbconsole-shared {
    margin-bottom: 12px;
    border: 1px solid #cbd8e8;
    border-left: 3px solid #7d9fd0;
    border-bottom: 3px solid #7d9fd0;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 1px 3px rgba(31, 45, 61, 0.06);
    overflow: hidden;
}

.dbconsole-shared__head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
}

.dbconsole-shared__info {
    flex: 1 1 auto;
    min-width: 0;
}

.dbconsole-shared__name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 标签另起一行（放在名称下方），避免挤压名称 */
.dbconsole-shared__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.dbconsole-shared__other {
    font-size: 12px;
    font-weight: 400;
    color: #aab4c4;
}

.dbconsole-shared__desc {
    font-size: 12px;
    color: #8a94a6;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 操作区：载入按钮 + 更多操作菜单 */
.dbconsole-shared__ops {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
}

/* 操作菜单项配色：沿用 Element Plus 主题色，不另行调色 */
.dbconsole-mi--share {
    color: var(--el-color-success);
}

.dbconsole-mi--delete {
    color: var(--el-color-danger);
}

/* 被禁用的菜单项统一置灰，避免主题色盖住禁用态 */
.dbconsole-mi.is-disabled {
    color: var(--el-text-color-disabled);
}
</style>

<!-- 弹窗样式（复用 dbman-dialog 前缀，与其他页面一致） -->
<style>
.dbman-dialog .dbman-switch-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dbman-dialog .dbman-tips {
    font-size: 12px;
    color: #aab4c4;
}

.dbman-dialog__foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.dbman-dialog__foot .el-button {
    min-width: 96px;
    height: 38px;
    margin-left: 0;
    border-radius: 10px;
}

/* ===== 「收藏结果」弹框（append-to-body，样式须挂 .dbman-dialog 下） ===== */
/* 固定高度 80vh（上下各留 9vh）：标题固定、内容区内部滚动。高度固定后展开卡片
   不再改变弹框高度，配合 scrollbar-gutter 消除滚动条闪烁。 */
.dbman-dialog.dbman-dialog--favlist {
    display: flex;
    flex-direction: column;
    height: 80vh;
    margin: 9vh auto;
}

.dbman-dialog--favlist .el-dialog__header {
    flex: 0 0 auto;
}

.dbman-dialog--favlist .el-dialog__body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.dbman-dialog .favx {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
}

/* 顶部工具条：块名 + 组数 + 全部展开 */
.dbman-dialog .favx__toolbar {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eef1f6;
}

.dbman-dialog .favx__block {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbman-dialog .favx__count {
    flex: 0 0 auto;
    padding: 1px 8px;
    border-radius: 999px;
    background-color: #eef3fc;
    color: #2f6fed;
    font-size: 12px;
}

.dbman-dialog .favx__toolbar .el-button {
    margin-left: auto;
}

/* 卡片列表：内部滚动 */
.dbman-dialog .favx__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    scrollbar-gutter: stable;
    padding: 10px 2px 2px;
}

/* 单张收藏卡片 */
.dbman-dialog .favx-card {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
    border: 1px solid #e6eaf0;
    border-radius: 10px;
    background-color: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.dbman-dialog .favx-card:hover {
    border-color: #cfdcf3;
    box-shadow: 0 6px 18px -12px rgba(28, 47, 84, 0.45);
}

.dbman-dialog .favx-card.is-open {
    border-color: #b7cdf5;
    box-shadow: 0 8px 22px -14px rgba(47, 111, 237, 0.6);
}

.dbman-dialog .favx-card__head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    cursor: pointer;
}

/* 左侧：时间（对比核心信息） */
.dbman-dialog .favx-card__time {
    flex: 0 0 auto;
    width: 86px;
    text-align: right;
}

.dbman-dialog .favx-card__date {
    display: block;
    font-size: 12px;
    color: #8a94a6;
}

.dbman-dialog .favx-card__clock {
    display: block;
    margin-top: 1px;
    font-size: 15px;
    font-weight: 700;
    color: #2f6fed;
    font-variant-numeric: tabular-nums;
}

/* 中间：名称 + 说明（左侧强调竖线，展开时高亮） */
.dbman-dialog .favx-card__main {
    flex: 1 1 auto;
    min-width: 0;
    padding-left: 12px;
    border-left: 2px solid #eef1f6;
}

.dbman-dialog .favx-card.is-open .favx-card__main {
    border-left-color: #2f6fed;
}

.dbman-dialog .favx-card__name {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.dbman-dialog .favx-card__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbman-dialog .favx-card__rows {
    flex: 0 0 auto;
    padding: 0 6px;
    border-radius: 999px;
    background-color: #f1f4f9;
    color: #5b6a80;
    font-size: 12px;
}

.dbman-dialog .favx-card__other {
    flex: 0 0 auto;
    font-size: 12px;
    color: #aab4c4;
}

.dbman-dialog .favx-card__desc {
    margin-top: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: #8a94a6;
}

/* 右侧：操作 + 展开箭头 */
.dbman-dialog .favx-card__ops {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
}

.dbman-dialog .favx-card__ops .el-button + .el-button {
    margin-left: 0;
}

.dbman-dialog .favx-card__arrow {
    color: #aab4c4;
    transition: transform 0.2s;
}

.dbman-dialog .favx-card__arrow.is-open {
    transform: rotate(90deg);
}

/* 展开的数据表 */
.dbman-dialog .favx-card__body {
    padding: 0 12px 12px;
}

/* 收藏卡片：展开后的完整语句 */
.dbman-dialog .favx-card__sql {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    background-color: #f7f9fc;
}

.dbman-dialog .favx-card__sql-label {
    flex: 0 0 auto;
    font-size: 12px;
    line-height: 18px;
    color: #8a94a6;
}

.dbman-dialog .favx-card__sql-text {
    flex: 1 1 auto;
    margin: 0;
    max-height: 96px;
    overflow: auto;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
    font-size: 12px;
    line-height: 18px;
    color: #33405a;
    white-space: pre-wrap;
    word-break: break-all;
}

/* 「收藏所选行」弹框里的执行语句预览（只读） */
.dbman-dialog .dbman-sql-view {
    width: 100%;
    max-height: 96px;
    overflow: auto;
    padding: 6px 8px;
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    background-color: #f7f9fc;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
    font-size: 12px;
    line-height: 18px;
    color: #33405a;
    white-space: pre-wrap;
    word-break: break-all;
}
</style>
